import type { Row } from "./types.js";

/**
 * 설정 잔여 섹션(SecuritySection / SyncSection / BackupSection) UI 문자열.
 * 키 접두사: sec.* / sync.* / backup.*
 */
export const settingsExtraMessages = {
  // ── Security (sec.*) ──────────────────────────────────────────────
  "sec.title": {
    ko: "보안 (API 키 보호)", "en-US": "Security (API key protection)",
    ja: "セキュリティ（API キー保護）", zh: "安全（API 密钥保护）",
    fr: "Sécurité (protection des clés API)", de: "Sicherheit (API-Schlüsselschutz)",
    es: "Seguridad (protección de claves API)", it: "Sicurezza (protezione chiavi API)",
    pt: "Segurança (proteção de chaves API)", "pt-BR": "Segurança (proteção de chaves de API)",
    ru: "Безопасность (защита API-ключей)", ar: "الأمان (حماية مفاتيح API)",
    hi: "सुरक्षा (API कुंजी सुरक्षा)", id: "Keamanan (perlindungan kunci API)",
  },
  "sec.checking": {
    ko: "확인 중...", "en-US": "Checking...", ja: "確認中...", zh: "检查中...",
    fr: "Vérification...", de: "Wird geprüft...", es: "Comprobando...", it: "Verifica in corso...",
    pt: "A verificar...", "pt-BR": "Verificando...", ru: "Проверка...", ar: "جارٍ التحقق...",
    hi: "जाँच हो रही है...", id: "Memeriksa...",
  },
  "sec.statusSet": {
    ko: "패스프레이즈가 설정되어 있습니다. 키는 패스프레이즈로 암호화됩니다.",
    "en-US": "A passphrase is set. Your keys are encrypted with it.",
    ja: "パスフレーズが設定されています。キーはパスフレーズで暗号化されます。",
    zh: "已设置口令。密钥将使用该口令加密。",
    fr: "Une phrase secrète est définie. Vos clés sont chiffrées avec celle-ci.",
    de: "Eine Passphrase ist festgelegt. Deine Schlüssel werden damit verschlüsselt.",
    es: "Hay una frase de contraseña configurada. Tus claves se cifran con ella.",
    it: "È impostata una passphrase. Le tue chiavi vengono cifrate con essa.",
    pt: "Está definida uma frase-passe. As suas chaves são cifradas com ela.",
    "pt-BR": "Uma frase secreta está definida. Suas chaves são criptografadas com ela.",
    ru: "Парольная фраза задана. Ваши ключи зашифрованы с её помощью.",
    ar: "تم تعيين عبارة مرور. مفاتيحك مشفّرة بها.",
    hi: "एक पासफ़्रेज़ सेट है। आपकी कुंजियाँ इससे एन्क्रिप्ट होती हैं।",
    id: "Frasa sandi telah disetel. Kunci Anda dienkripsi dengannya.",
  },
  "sec.statusNone": {
    ko: "패스프레이즈 없음. 키는 기기 전용 비추출 키로 암호화됩니다.",
    "en-US": "No passphrase. Keys are encrypted with a device-only, non-extractable key.",
    ja: "パスフレーズなし。キーはこの端末専用の抽出不可キーで暗号化されます。",
    zh: "未设置口令。密钥将使用仅限本设备、不可导出的密钥加密。",
    fr: "Aucune phrase secrète. Les clés sont chiffrées avec une clé propre à l'appareil, non extractible.",
    de: "Keine Passphrase. Schlüssel werden mit einem geräteeigenen, nicht extrahierbaren Schlüssel verschlüsselt.",
    es: "Sin frase de contraseña. Las claves se cifran con una clave exclusiva del dispositivo y no extraíble.",
    it: "Nessuna passphrase. Le chiavi sono cifrate con una chiave non estraibile, esclusiva del dispositivo.",
    pt: "Sem frase-passe. As chaves são cifradas com uma chave não extraível, exclusiva do dispositivo.",
    "pt-BR": "Sem frase secreta. As chaves são criptografadas com uma chave não extraível, exclusiva do dispositivo.",
    ru: "Без парольной фразы. Ключи шифруются ключом, привязанным к устройству и не подлежащим извлечению.",
    ar: "لا توجد عبارة مرور. تُشفّر المفاتيح بمفتاح خاص بالجهاز وغير قابل للاستخراج.",
    hi: "कोई पासफ़्रेज़ नहीं। कुंजियाँ केवल-डिवाइस, गैर-निष्कर्षणीय कुंजी से एन्क्रिप्ट होती हैं।",
    id: "Tanpa frasa sandi. Kunci dienkripsi dengan kunci khusus perangkat yang tidak dapat diekstrak.",
  },
  "sec.setPassphrase": {
    ko: "패스프레이즈 설정", "en-US": "Set passphrase", ja: "パスフレーズを設定",
    zh: "设置口令", fr: "Définir une phrase secrète", de: "Passphrase festlegen",
    es: "Definir frase de contraseña", it: "Imposta passphrase",
    pt: "Definir frase-passe", "pt-BR": "Definir frase secreta", ru: "Задать парольную фразу",
    ar: "تعيين عبارة مرور", hi: "पासफ़्रेज़ सेट करें", id: "Setel frasa sandi",
  },
  "sec.change": {
    ko: "변경", "en-US": "Change", ja: "変更", zh: "更改", fr: "Modifier",
    de: "Ändern", es: "Cambiar", it: "Modifica", pt: "Alterar", "pt-BR": "Alterar",
    ru: "Изменить", ar: "تغيير", hi: "बदलें", id: "Ubah",
  },
  "sec.remove": {
    ko: "해제", "en-US": "Remove", ja: "解除", zh: "解除", fr: "Désactiver",
    de: "Entfernen", es: "Quitar", it: "Rimuovi", pt: "Remover", "pt-BR": "Remover",
    ru: "Снять", ar: "إزالة", hi: "हटाएँ", id: "Hapus",
  },
  "sec.lossWarning": {
    ko: "패스프레이즈를 분실하면 저장된 키를 복구할 수 없습니다.",
    "en-US": "If you lose your passphrase, your stored keys cannot be recovered.",
    ja: "パスフレーズを紛失すると、保存されたキーは復元できません。",
    zh: "若遗失口令，已保存的密钥将无法恢复。",
    fr: "Si vous perdez votre phrase secrète, vos clés enregistrées seront irrécupérables.",
    de: "Wenn du deine Passphrase verlierst, können deine gespeicherten Schlüssel nicht wiederhergestellt werden.",
    es: "Si pierdes tu frase de contraseña, no podrás recuperar las claves guardadas.",
    it: "Se perdi la passphrase, le chiavi salvate non potranno essere recuperate.",
    pt: "Se perder a frase-passe, as chaves guardadas não podem ser recuperadas.",
    "pt-BR": "Se você perder a frase secreta, as chaves salvas não poderão ser recuperadas.",
    ru: "Если вы потеряете парольную фразу, восстановить сохранённые ключи будет невозможно.",
    ar: "إذا فقدت عبارة المرور، فلن يمكن استعادة المفاتيح المحفوظة.",
    hi: "यदि आप अपना पासफ़्रेज़ खो देते हैं, तो सहेजी गई कुंजियाँ पुनर्प्राप्त नहीं की जा सकतीं।",
    id: "Jika frasa sandi hilang, kunci tersimpan tidak dapat dipulihkan.",
  },
  "sec.errMismatch": {
    ko: "새 패스프레이즈가 일치하지 않습니다.",
    "en-US": "The new passphrases don't match.",
    ja: "新しいパスフレーズが一致しません。",
    zh: "两次输入的新口令不一致。",
    fr: "Les nouvelles phrases secrètes ne correspondent pas.",
    de: "Die neuen Passphrasen stimmen nicht überein.",
    es: "Las nuevas frases de contraseña no coinciden.",
    it: "Le nuove passphrase non corrispondono.",
    pt: "As novas frases-passe não coincidem.",
    "pt-BR": "As novas frases secretas não coincidem.",
    ru: "Новые парольные фразы не совпадают.",
    ar: "عبارتا المرور الجديدتان غير متطابقتين.",
    hi: "नए पासफ़्रेज़ मेल नहीं खाते।",
    id: "Frasa sandi baru tidak cocok.",
  },
  "sec.errTooShort": {
    ko: "패스프레이즈는 4자 이상이어야 합니다.",
    "en-US": "The passphrase must be at least 4 characters.",
    ja: "パスフレーズは 4 文字以上にしてください。",
    zh: "口令至少需要 4 个字符。",
    fr: "La phrase secrète doit comporter au moins 4 caractères.",
    de: "Die Passphrase muss mindestens 4 Zeichen lang sein.",
    es: "La frase de contraseña debe tener al menos 4 caracteres.",
    it: "La passphrase deve contenere almeno 4 caratteri.",
    pt: "A frase-passe deve ter pelo menos 4 caracteres.",
    "pt-BR": "A frase secreta deve ter pelo menos 4 caracteres.",
    ru: "Парольная фраза должна содержать не менее 4 символов.",
    ar: "يجب أن تتكوّن عبارة المرور من 4 أحرف على الأقل.",
    hi: "पासफ़्रेज़ कम से कम 4 अक्षरों का होना चाहिए।",
    id: "Frasa sandi harus minimal 4 karakter.",
  },
  "sec.currentLabel": {
    ko: "현재 패스프레이즈", "en-US": "Current passphrase",
    ja: "現在のパスフレーズ", zh: "当前口令",
    fr: "Phrase secrète actuelle", de: "Aktuelle Passphrase",
    es: "Frase de contraseña actual", it: "Passphrase attuale",
    pt: "Frase-passe atual", "pt-BR": "Frase secreta atual",
    ru: "Текущая парольная фраза", ar: "عبارة المرور الحالية",
    hi: "वर्तमान पासफ़्रेज़", id: "Frasa sandi saat ini",
  },
  "sec.newLabel": {
    ko: "새 패스프레이즈", "en-US": "New passphrase",
    ja: "新しいパスフレーズ", zh: "新口令",
    fr: "Nouvelle phrase secrète", de: "Neue Passphrase",
    es: "Nueva frase de contraseña", it: "Nuova passphrase",
    pt: "Nova frase-passe", "pt-BR": "Nova frase secreta",
    ru: "Новая парольная фраза", ar: "عبارة المرور الجديدة",
    hi: "नया पासफ़्रेज़", id: "Frasa sandi baru",
  },
  "sec.confirmLabel": {
    ko: "새 패스프레이즈 확인", "en-US": "Confirm new passphrase",
    ja: "新しいパスフレーズの確認", zh: "确认新口令",
    fr: "Confirmer la nouvelle phrase secrète", de: "Neue Passphrase bestätigen",
    es: "Confirmar nueva frase de contraseña", it: "Conferma nuova passphrase",
    pt: "Confirmar nova frase-passe", "pt-BR": "Confirmar nova frase secreta",
    ru: "Подтвердите новую парольную фразу", ar: "تأكيد عبارة المرور الجديدة",
    hi: "नया पासफ़्रेज़ पुष्टि करें", id: "Konfirmasi frasa sandi baru",
  },
  "sec.processing": {
    ko: "처리 중...", "en-US": "Processing...", ja: "処理中...", zh: "处理中...",
    fr: "Traitement...", de: "Wird verarbeitet...", es: "Procesando...", it: "Elaborazione...",
    pt: "A processar...", "pt-BR": "Processando...", ru: "Обработка...", ar: "جارٍ المعالجة...",
    hi: "प्रोसेस हो रहा है...", id: "Memproses...",
  },

  // ── Sync (sync.*) ─────────────────────────────────────────────────
  "sync.title": {
    ko: "동기화 (병합)", "en-US": "Sync (merge)", ja: "同期（マージ）",
    zh: "同步（合并）", fr: "Synchronisation (fusion)", de: "Synchronisierung (Zusammenführen)",
    es: "Sincronización (fusión)", it: "Sincronizzazione (unione)",
    pt: "Sincronização (fusão)", "pt-BR": "Sincronização (mesclagem)",
    ru: "Синхронизация (слияние)", ar: "المزامنة (دمج)",
    hi: "सिंक (मर्ज)", id: "Sinkronisasi (gabung)",
  },
  "sync.mergeDesc": {
    ko: "두 기기의 변경을 병합합니다(덮어쓰기 아님). 같은 항목이 양쪽에서 바뀐 경우에만 아래 우선순위로 해소합니다. (API 키는 포함되지 않습니다)",
    "en-US": "Merges changes from two devices (not an overwrite). Only when the same item changed on both sides is it resolved by the priority below. (API keys are not included.)",
    ja: "2 台の端末の変更をマージします（上書きではありません）。同じ項目が両方で変更された場合のみ、下記の優先順位で解決します。（API キーは含まれません）",
    zh: "合并两台设备的更改（而非覆盖）。仅当同一项目在两端都被修改时，才按下面的优先级解决。（不包含 API 密钥）",
    fr: "Fusionne les modifications de deux appareils (sans écrasement). Seul un même élément modifié des deux côtés est résolu selon la priorité ci-dessous. (Les clés API ne sont pas incluses.)",
    de: "Führt Änderungen von zwei Geräten zusammen (kein Überschreiben). Nur wenn dasselbe Element auf beiden Seiten geändert wurde, entscheidet die Priorität unten. (API-Schlüssel sind nicht enthalten.)",
    es: "Fusiona los cambios de dos dispositivos (no es una sobrescritura). Solo cuando el mismo elemento cambió en ambos lados se resuelve según la prioridad de abajo. (Las claves API no se incluyen.)",
    it: "Unisce le modifiche di due dispositivi (non è una sovrascrittura). Solo quando lo stesso elemento è cambiato su entrambi i lati viene risolto con la priorità qui sotto. (Le chiavi API non sono incluse.)",
    pt: "Funde as alterações de dois dispositivos (não é uma substituição). Só quando o mesmo item muda dos dois lados é resolvido pela prioridade abaixo. (As chaves API não são incluídas.)",
    "pt-BR": "Mescla as alterações de dois dispositivos (não é uma substituição). Somente quando o mesmo item muda nos dois lados ele é resolvido pela prioridade abaixo. (As chaves de API não são incluídas.)",
    ru: "Объединяет изменения с двух устройств (не перезапись). Только если один и тот же элемент изменён с обеих сторон, конфликт решается по приоритету ниже. (API-ключи не включаются.)",
    ar: "يدمج التغييرات من جهازين (وليس استبدالاً). لا يُحلّ التعارض حسب الأولوية أدناه إلا عند تغيّر العنصر نفسه على الجانبين. (لا تُضمَّن مفاتيح API.)",
    hi: "दो डिवाइस के बदलावों को मर्ज करता है (ओवरराइट नहीं)। केवल जब वही आइटम दोनों ओर बदला हो, तो नीचे दी गई प्राथमिकता से हल किया जाता है। (API कुंजियाँ शामिल नहीं हैं।)",
    id: "Menggabungkan perubahan dari dua perangkat (bukan menimpa). Hanya jika item yang sama berubah di kedua sisi, konflik diselesaikan dengan prioritas di bawah. (Kunci API tidak disertakan.)",
  },
  "sync.conflictPriority": {
    ko: "충돌 시 우선순위", "en-US": "Conflict priority", ja: "競合時の優先順位",
    zh: "冲突时的优先级", fr: "Priorité en cas de conflit", de: "Priorität bei Konflikten",
    es: "Prioridad en conflictos", it: "Priorità in caso di conflitto",
    pt: "Prioridade em conflitos", "pt-BR": "Prioridade em conflitos",
    ru: "Приоритет при конфликте", ar: "الأولوية عند التعارض",
    hi: "टकराव में प्राथमिकता", id: "Prioritas saat konflik",
  },
  "sync.keepLocal": {
    ko: "내 변경 우선", "en-US": "Prefer my changes", ja: "自分の変更を優先",
    zh: "优先本地更改", fr: "Préférer mes modifications", de: "Eigene Änderungen bevorzugen",
    es: "Priorizar mis cambios", it: "Preferisci le mie modifiche",
    pt: "Preferir as minhas alterações", "pt-BR": "Preferir minhas alterações",
    ru: "Сохранять мои изменения", ar: "تفضيل تغييراتي",
    hi: "मेरे बदलाव प्राथमिक", id: "Utamakan perubahan saya",
  },
  "sync.keepRemote": {
    ko: "가져온 쪽 우선", "en-US": "Prefer imported changes", ja: "取り込んだ側を優先",
    zh: "优先导入的更改", fr: "Préférer les modifications importées", de: "Importierte Änderungen bevorzugen",
    es: "Priorizar los cambios importados", it: "Preferisci le modifiche importate",
    pt: "Preferir as alterações importadas", "pt-BR": "Preferir as alterações importadas",
    ru: "Сохранять импортированные изменения", ar: "تفضيل التغييرات المستوردة",
    hi: "आयातित बदलाव प्राथमिक", id: "Utamakan perubahan yang diimpor",
  },
  "sync.connected": {
    ko: "연결됨", "en-US": "Connected", ja: "接続済み", zh: "已连接",
    fr: "Connecté", de: "Verbunden", es: "Conectado", it: "Connesso",
    pt: "Ligado", "pt-BR": "Conectado", ru: "Подключено", ar: "متصل",
    hi: "कनेक्टेड", id: "Terhubung",
  },
  "sync.notConnected": {
    ko: "미연결", "en-US": "Not connected", ja: "未接続", zh: "未连接",
    fr: "Non connecté", de: "Nicht verbunden", es: "Sin conexión", it: "Non connesso",
    pt: "Não ligado", "pt-BR": "Não conectado", ru: "Не подключено", ar: "غير متصل",
    hi: "कनेक्ट नहीं", id: "Tidak terhubung",
  },
  "sync.googleNotConfigured": {
    ko: "Google 동기화는 빌드에 VITE_GOOGLE_CLIENT_ID가 주입돼야 활성화됩니다. 지금은 아래 파일 동기화를 사용하세요.",
    "en-US": "Google sync is enabled only when VITE_GOOGLE_CLIENT_ID is injected into the build. For now, use file sync below.",
    ja: "Google 同期は VITE_GOOGLE_CLIENT_ID がビルドに注入されている場合のみ有効になります。今は下のファイル同期をご利用ください。",
    zh: "仅当构建中注入 VITE_GOOGLE_CLIENT_ID 时，Google 同步才会启用。目前请使用下方的文件同步。",
    fr: "La synchronisation Google n'est activée que si VITE_GOOGLE_CLIENT_ID est injecté dans le build. Pour l'instant, utilisez la synchronisation par fichier ci-dessous.",
    de: "Die Google-Synchronisierung ist nur aktiv, wenn VITE_GOOGLE_CLIENT_ID in den Build eingebunden ist. Nutze vorerst die Dateisynchronisierung unten.",
    es: "La sincronización con Google solo se activa si se inyecta VITE_GOOGLE_CLIENT_ID en la compilación. Por ahora, usa la sincronización por archivo de abajo.",
    it: "La sincronizzazione Google si attiva solo se VITE_GOOGLE_CLIENT_ID è iniettato nella build. Per ora usa la sincronizzazione tramite file qui sotto.",
    pt: "A sincronização Google só é ativada se VITE_GOOGLE_CLIENT_ID for injetado na compilação. Por agora, use a sincronização por ficheiro abaixo.",
    "pt-BR": "A sincronização do Google só é ativada se VITE_GOOGLE_CLIENT_ID for injetado na compilação. Por enquanto, use a sincronização por arquivo abaixo.",
    ru: "Синхронизация с Google включается, только если VITE_GOOGLE_CLIENT_ID встроен в сборку. Пока используйте файловую синхронизацию ниже.",
    ar: "تُفعَّل مزامنة Google فقط عند حقن VITE_GOOGLE_CLIENT_ID في الإصدار. استخدم مزامنة الملفات أدناه في الوقت الحالي.",
    hi: "Google सिंक तभी सक्षम होता है जब बिल्ड में VITE_GOOGLE_CLIENT_ID इंजेक्ट किया गया हो। फ़िलहाल नीचे फ़ाइल सिंक का उपयोग करें।",
    id: "Sinkronisasi Google hanya aktif jika VITE_GOOGLE_CLIENT_ID disuntikkan ke build. Untuk saat ini, gunakan sinkronisasi file di bawah.",
  },
  "sync.syncWithDrive": {
    ko: "Drive와 동기화", "en-US": "Sync with Drive", ja: "Drive と同期",
    zh: "与 Drive 同步", fr: "Synchroniser avec Drive", de: "Mit Drive synchronisieren",
    es: "Sincronizar con Drive", it: "Sincronizza con Drive",
    pt: "Sincronizar com o Drive", "pt-BR": "Sincronizar com o Drive",
    ru: "Синхронизировать с Drive", ar: "المزامنة مع Drive",
    hi: "Drive के साथ सिंक करें", id: "Sinkronkan dengan Drive",
  },
  "sync.disconnect": {
    ko: "연결 해제", "en-US": "Disconnect", ja: "接続を解除", zh: "断开连接",
    fr: "Déconnecter", de: "Trennen", es: "Desconectar", it: "Disconnetti",
    pt: "Desligar", "pt-BR": "Desconectar", ru: "Отключить", ar: "قطع الاتصال",
    hi: "डिस्कनेक्ट करें", id: "Putuskan",
  },
  "sync.connectGoogle": {
    ko: "Google 계정 연결", "en-US": "Connect Google account", ja: "Google アカウントを連携",
    zh: "连接 Google 账号", fr: "Connecter un compte Google", de: "Google-Konto verbinden",
    es: "Conectar cuenta de Google", it: "Connetti account Google",
    pt: "Ligar conta Google", "pt-BR": "Conectar conta do Google",
    ru: "Подключить аккаунт Google", ar: "ربط حساب Google",
    hi: "Google खाता कनेक्ट करें", id: "Hubungkan akun Google",
  },
  "sync.autoSync": {
    ko: "자동 동기화", "en-US": "Auto sync", ja: "自動同期", zh: "自动同步",
    fr: "Synchronisation automatique", de: "Automatische Synchronisierung",
    es: "Sincronización automática", it: "Sincronizzazione automatica",
    pt: "Sincronização automática", "pt-BR": "Sincronização automática",
    ru: "Автосинхронизация", ar: "المزامنة التلقائية",
    hi: "स्वतः सिंक", id: "Sinkronisasi otomatis",
  },
  "sync.autoSyncDesc": {
    ko: "변경 후 잠시 뒤 자동으로 Drive와 병합합니다. 충돌은 내 변경을 우선합니다.",
    "en-US": "Merges with Drive automatically a short while after changes. Conflicts prefer your changes.",
    ja: "変更の少し後に自動で Drive とマージします。競合は自分の変更を優先します。",
    zh: "更改后稍候将自动与 Drive 合并。冲突时优先保留你的更改。",
    fr: "Fusionne automatiquement avec Drive peu après les modifications. En cas de conflit, vos modifications sont prioritaires.",
    de: "Führt kurz nach Änderungen automatisch mit Drive zusammen. Bei Konflikten haben deine Änderungen Vorrang.",
    es: "Se fusiona con Drive automáticamente poco después de los cambios. En conflictos se priorizan tus cambios.",
    it: "Si unisce a Drive automaticamente poco dopo le modifiche. In caso di conflitto prevalgono le tue modifiche.",
    pt: "Funde com o Drive automaticamente pouco depois das alterações. Em conflitos, as suas alterações têm prioridade.",
    "pt-BR": "Mescla com o Drive automaticamente pouco depois das alterações. Em conflitos, suas alterações têm prioridade.",
    ru: "Автоматически объединяет с Drive вскоре после изменений. При конфликтах приоритет у ваших изменений.",
    ar: "يدمج مع Drive تلقائيًا بعد التغييرات بقليل. تُفضَّل تغييراتك عند التعارض.",
    hi: "बदलावों के थोड़ी देर बाद Drive के साथ स्वतः मर्ज करता है। टकराव में आपके बदलाव प्राथमिक रहते हैं।",
    id: "Menggabungkan dengan Drive secara otomatis sesaat setelah ada perubahan. Konflik mengutamakan perubahan Anda.",
  },
  "sync.interval.10s": {
    ko: "10초", "en-US": "10 sec", ja: "10秒", zh: "10秒",
    fr: "10 s", de: "10 Sek.", es: "10 s", it: "10 sec",
    pt: "10 s", "pt-BR": "10 s", ru: "10 сек", ar: "١٠ ثوانٍ",
    hi: "10s", id: "10d",
  },
  "sync.interval.1m": {
    ko: "1분", "en-US": "1 min", ja: "1分", zh: "1分钟", fr: "1 min", de: "1 Min.", es: "1 min", it: "1 min", pt: "1 min", "pt-BR": "1 min", ru: "1 мин", ar: "١ دقيقة", hi: "1 मिनट", id: "1 mnt"
  },
  "sync.interval.5m": {
    ko: "5분", "en-US": "5 min", ja: "5分", zh: "5分钟", fr: "5 min", de: "5 Min.", es: "5 min", it: "5 min", pt: "5 min", "pt-BR": "5 min", ru: "5 мин", ar: "٥ دقائق", hi: "5 मिनट", id: "5 mnt"
  },
  "sync.interval.10m": {
    ko: "10분", "en-US": "10 min", ja: "10分", zh: "10分钟", fr: "10 min", de: "10 Min.", es: "10 min", it: "10 min", pt: "10 min", "pt-BR": "10 min", ru: "10 мин", ar: "١٠ دقائق", hi: "10 मिनट", id: "10 mnt"
  },
  "sync.interval.30m": {
    ko: "30분", "en-US": "30 min", ja: "30分", zh: "30分钟", fr: "30 min", de: "30 Min.", es: "30 min", it: "30 min", pt: "30 min", "pt-BR": "30 min", ru: "30 мин", ar: "٣٠ دقيقة", hi: "30 मिनट", id: "30 mnt"
  },
  "sync.interval.1h": {
    ko: "1시간", "en-US": "1 hr", ja: "1時間", zh: "1小时", fr: "1 h", de: "1 Std.", es: "1 h", it: "1 h", pt: "1 h", "pt-BR": "1 h", ru: "1 час", ar: "١ ساعة", hi: "1 घंटा", id: "1 jam"
  },
  "sync.lastSynced": {
    ko: "마지막 동기화: {time}", "en-US": "Last synced: {time}",
    ja: "最終同期: {time}", zh: "上次同步：{time}",
    fr: "Dernière synchronisation : {time}", de: "Zuletzt synchronisiert: {time}",
    es: "Última sincronización: {time}", it: "Ultima sincronizzazione: {time}",
    pt: "Última sincronização: {time}", "pt-BR": "Última sincronização: {time}",
    ru: "Последняя синхронизация: {time}", ar: "آخر مزامنة: {time}",
    hi: "अंतिम सिंक: {time}", id: "Terakhir disinkronkan: {time}",
  },
  "sync.fileSyncLabel": {
    ko: "파일로 주고받기 (OAuth 불필요)", "en-US": "Exchange via file (no OAuth)",
    ja: "ファイルでやり取り（OAuth 不要）", zh: "通过文件交换（无需 OAuth）",
    fr: "Échange par fichier (sans OAuth)", de: "Über Datei austauschen (kein OAuth)",
    es: "Intercambio por archivo (sin OAuth)", it: "Scambio tramite file (senza OAuth)",
    pt: "Trocar por ficheiro (sem OAuth)", "pt-BR": "Trocar por arquivo (sem OAuth)",
    ru: "Обмен через файл (без OAuth)", ar: "التبادل عبر ملف (بدون OAuth)",
    hi: "फ़ाइल से आदान-प्रदान (OAuth नहीं)", id: "Tukar lewat file (tanpa OAuth)",
  },
  "sync.importMerge": {
    ko: "가져와 병합", "en-US": "Import and merge", ja: "取り込んでマージ",
    zh: "导入并合并", fr: "Importer et fusionner", de: "Importieren und zusammenführen",
    es: "Importar y fusionar", it: "Importa e unisci",
    pt: "Importar e fundir", "pt-BR": "Importar e mesclar",
    ru: "Импорт и слияние", ar: "استيراد ودمج",
    hi: "आयात कर मर्ज करें", id: "Impor dan gabungkan",
  },
  "sync.exportDone": {
    ko: "동기화 파일을 저장했습니다. 다른 기기에서 '가져와 병합'으로 합치세요.",
    "en-US": "Sync file downloaded. On another device, combine it with \"Import and merge\".",
    ja: "同期ファイルをダウンロードしました。別の端末で「取り込んでマージ」で結合してください。",
    zh: "已下载同步文件。请在另一台设备上用“导入并合并”进行合并。",
    fr: "Fichier de synchronisation téléchargé. Sur un autre appareil, combinez-le via « Importer et fusionner ».",
    de: "Synchronisierungsdatei heruntergeladen. Füge sie auf einem anderen Gerät über „Importieren und zusammenführen“ zusammen.",
    es: "Archivo de sincronización descargado. En otro dispositivo, combínalo con «Importar y fusionar».",
    it: "File di sincronizzazione scaricato. Su un altro dispositivo, combinalo con «Importa e unisci».",
    pt: "Ficheiro de sincronização descarregado. Noutro dispositivo, combine-o com \"Importar e fundir\".",
    "pt-BR": "Arquivo de sincronização baixado. Em outro dispositivo, combine-o com \"Importar e mesclar\".",
    ru: "Файл синхронизации загружен. На другом устройстве объедините его через «Импорт и слияние».",
    ar: "تم تنزيل ملف المزامنة. على جهاز آخر، ادمجه عبر \"استيراد ودمج\".",
    hi: "सिंक फ़ाइल डाउनलोड हो गई। दूसरे डिवाइस पर \"आयात कर मर्ज करें\" से संयोजित करें।",
    id: "File sinkronisasi telah diunduh. Di perangkat lain, gabungkan dengan \"Impor dan gabungkan\".",
  },
  "sync.exportFail": {
    ko: "내보내기 실패: {error}", "en-US": "Export failed: {error}",
    ja: "エクスポートに失敗しました: {error}", zh: "导出失败：{error}",
    fr: "Échec de l'exportation : {error}", de: "Export fehlgeschlagen: {error}",
    es: "Error al exportar: {error}", it: "Esportazione non riuscita: {error}",
    pt: "Falha na exportação: {error}", "pt-BR": "Falha na exportação: {error}",
    ru: "Не удалось экспортировать: {error}", ar: "فشل التصدير: {error}",
    hi: "निर्यात विफल: {error}", id: "Ekspor gagal: {error}",
  },
  "sync.mergeDone": {
    ko: "병합이 완료되었습니다: 받음 {pulled} · 보냄 {pushed} · 충돌 {conflicts}. 변경 사항을 보려면 새로고침하세요.",
    "en-US": "Merge complete: pulled {pulled} · pushed {pushed} · conflicts {conflicts}. Refresh to apply changes.",
    ja: "マージ完了: 受信 {pulled} · 送信 {pushed} · 競合 {conflicts}。変更を反映するには再読み込みしてください。",
    zh: "合并完成：拉取 {pulled} · 推送 {pushed} · 冲突 {conflicts}。请刷新以应用更改。",
    fr: "Fusion terminée : reçus {pulled} · envoyés {pushed} · conflits {conflicts}. Actualisez pour appliquer.",
    de: "Zusammenführung abgeschlossen: empfangen {pulled} · gesendet {pushed} · Konflikte {conflicts}. Zum Übernehmen neu laden.",
    es: "Fusión completa: recibidos {pulled} · enviados {pushed} · conflictos {conflicts}. Actualiza para aplicar los cambios.",
    it: "Unione completata: ricevuti {pulled} · inviati {pushed} · conflitti {conflicts}. Aggiorna per applicare le modifiche.",
    pt: "Fusão concluída: recebidos {pulled} · enviados {pushed} · conflitos {conflicts}. Atualize para aplicar as alterações.",
    "pt-BR": "Mesclagem concluída: recebidos {pulled} · enviados {pushed} · conflitos {conflicts}. Atualize para aplicar as alterações.",
    ru: "Слияние завершено: получено {pulled} · отправлено {pushed} · конфликтов {conflicts}. Обновите страницу, чтобы применить изменения.",
    ar: "اكتمل الدمج: مستلَم {pulled} · مُرسَل {pushed} · تعارضات {conflicts}. حدّث الصفحة لتطبيق التغييرات.",
    hi: "मर्ज पूर्ण: प्राप्त {pulled} · भेजा {pushed} · टकराव {conflicts}। बदलाव लागू करने हेतु रिफ़्रेश करें।",
    id: "Penggabungan selesai: diterima {pulled} · dikirim {pushed} · konflik {conflicts}. Segarkan untuk menerapkan perubahan.",
  },
  "sync.mergeFail": {
    ko: "병합 실패: {error}", "en-US": "Merge failed: {error}",
    ja: "マージに失敗しました: {error}", zh: "合并失败：{error}",
    fr: "Échec de la fusion : {error}", de: "Zusammenführen fehlgeschlagen: {error}",
    es: "Error al fusionar: {error}", it: "Unione non riuscita: {error}",
    pt: "Falha na fusão: {error}", "pt-BR": "Falha na mesclagem: {error}",
    ru: "Не удалось выполнить слияние: {error}", ar: "فشل الدمج: {error}",
    hi: "मर्ज विफल: {error}", id: "Penggabungan gagal: {error}",
  },
  "sync.googleConnected": {
    ko: "Google 계정을 연결했습니다. 이제 'Drive와 동기화'로 병합할 수 있어요.",
    "en-US": "Google account connected. You can now merge with \"Sync with Drive\".",
    ja: "Google アカウントを連携しました。これで「Drive と同期」でマージできます。",
    zh: "已连接 Google 账号。现在可以用“与 Drive 同步”进行合并。",
    fr: "Compte Google connecté. Vous pouvez maintenant fusionner via « Synchroniser avec Drive ».",
    de: "Google-Konto verbunden. Du kannst jetzt über „Mit Drive synchronisieren“ zusammenführen.",
    es: "Cuenta de Google conectada. Ahora puedes fusionar con «Sincronizar con Drive».",
    it: "Account Google connesso. Ora puoi unire con «Sincronizza con Drive».",
    pt: "Conta Google ligada. Já pode fundir com \"Sincronizar com o Drive\".",
    "pt-BR": "Conta do Google conectada. Agora você pode mesclar com \"Sincronizar com o Drive\".",
    ru: "Аккаунт Google подключён. Теперь можно объединять через «Синхронизировать с Drive».",
    ar: "تم ربط حساب Google. يمكنك الآن الدمج عبر \"المزامنة مع Drive\".",
    hi: "Google खाता कनेक्ट हो गया। अब आप \"Drive के साथ सिंक करें\" से मर्ज कर सकते हैं।",
    id: "Akun Google terhubung. Anda kini dapat menggabungkan lewat \"Sinkronkan dengan Drive\".",
  },
  "sync.googleConnectFail": {
    ko: "Google 연결 실패: {error}", "en-US": "Google connection failed: {error}",
    ja: "Google 連携に失敗しました: {error}", zh: "连接 Google 失败：{error}",
    fr: "Échec de la connexion Google : {error}", de: "Google-Verbindung fehlgeschlagen: {error}",
    es: "Error al conectar con Google: {error}", it: "Connessione a Google non riuscita: {error}",
    pt: "Falha ao ligar ao Google: {error}", "pt-BR": "Falha ao conectar ao Google: {error}",
    ru: "Не удалось подключить Google: {error}", ar: "فشل الاتصال بـ Google: {error}",
    hi: "Google कनेक्शन विफल: {error}", id: "Koneksi Google gagal: {error}",
  },
  "sync.googleDisconnected": {
    ko: "Google 연결을 해제했습니다.", "en-US": "Google account disconnected.",
    ja: "Google 連携を解除しました。", zh: "已断开 Google 连接。",
    fr: "Compte Google déconnecté.", de: "Google-Konto getrennt.",
    es: "Cuenta de Google desconectada.", it: "Account Google disconnesso.",
    pt: "Conta Google desligada.", "pt-BR": "Conta do Google desconectada.",
    ru: "Аккаунт Google отключён.", ar: "تم قطع الاتصال بحساب Google.",
    hi: "Google खाता डिस्कनेक्ट हो गया।", id: "Akun Google diputuskan.",
  },
  "sync.disconnectFail": {
    ko: "연결 해제 실패: {error}", "en-US": "Disconnect failed: {error}",
    ja: "接続解除に失敗しました: {error}", zh: "断开连接失败：{error}",
    fr: "Échec de la déconnexion : {error}", de: "Trennen fehlgeschlagen: {error}",
    es: "Error al desconectar: {error}", it: "Disconnessione non riuscita: {error}",
    pt: "Falha ao desligar: {error}", "pt-BR": "Falha ao desconectar: {error}",
    ru: "Не удалось отключить: {error}", ar: "فشل قطع الاتصال: {error}",
    hi: "डिस्कनेक्ट विफल: {error}", id: "Pemutusan gagal: {error}",
  },
  "sync.driveDone": {
    ko: "Drive 동기화가 완료되었습니다: 받음 {pulled} · 보냄 {pushed} · 충돌 {conflicts}. 변경 사항을 보려면 새로고침하세요.",
    "en-US": "Drive sync complete: pulled {pulled} · pushed {pushed} · conflicts {conflicts}. Refresh to apply changes.",
    ja: "Drive 同期完了: 受信 {pulled} · 送信 {pushed} · 競合 {conflicts}。変更を反映するには再読み込みしてください。",
    zh: "Drive 同步完成：拉取 {pulled} · 推送 {pushed} · 冲突 {conflicts}。请刷新以应用更改。",
    fr: "Synchronisation Drive terminée : reçus {pulled} · envoyés {pushed} · conflits {conflicts}. Actualisez pour appliquer.",
    de: "Drive-Synchronisierung abgeschlossen: empfangen {pulled} · gesendet {pushed} · Konflikte {conflicts}. Zum Übernehmen neu laden.",
    es: "Sincronización con Drive completa: recibidos {pulled} · enviados {pushed} · conflictos {conflicts}. Actualiza para aplicar los cambios.",
    it: "Sincronizzazione Drive completata: ricevuti {pulled} · inviati {pushed} · conflitti {conflicts}. Aggiorna per applicare le modifiche.",
    pt: "Sincronização com o Drive concluída: recebidos {pulled} · enviados {pushed} · conflitos {conflicts}. Atualize para aplicar as alterações.",
    "pt-BR": "Sincronização com o Drive concluída: recebidos {pulled} · enviados {pushed} · conflitos {conflicts}. Atualize para aplicar as alterações.",
    ru: "Синхронизация с Drive завершена: получено {pulled} · отправлено {pushed} · конфликтов {conflicts}. Обновите страницу, чтобы применить изменения.",
    ar: "اكتملت مزامنة Drive: مستلَم {pulled} · مُرسَل {pushed} · تعارضات {conflicts}. حدّث الصفحة لتطبيق التغييرات.",
    hi: "Drive सिंक पूर्ण: प्राप्त {pulled} · भेजा {pushed} · टकराव {conflicts}। बदलाव लागू करने हेतु रिफ़्रेश करें।",
    id: "Sinkronisasi Drive selesai: diterima {pulled} · dikirim {pushed} · konflik {conflicts}. Segarkan untuk menerapkan perubahan.",
  },
  "sync.driveFail": {
    ko: "Drive 동기화 실패: {error}", "en-US": "Drive sync failed: {error}",
    ja: "Drive 同期に失敗しました: {error}", zh: "Drive 同步失败：{error}",
    fr: "Échec de la synchronisation Drive : {error}", de: "Drive-Synchronisierung fehlgeschlagen: {error}",
    es: "Error al sincronizar con Drive: {error}", it: "Sincronizzazione Drive non riuscita: {error}",
    pt: "Falha na sincronização com o Drive: {error}", "pt-BR": "Falha na sincronização com o Drive: {error}",
    ru: "Не удалось синхронизировать с Drive: {error}", ar: "فشلت مزامنة Drive: {error}",
    hi: "Drive सिंक विफल: {error}", id: "Sinkronisasi Drive gagal: {error}",
  },

  // ── Backup (backup.*) ─────────────────────────────────────────────
  "backup.title": {
    ko: "백업 & 복원", "en-US": "Backup & restore", ja: "バックアップと復元",
    zh: "备份与恢复", fr: "Sauvegarde et restauration", de: "Sicherung & Wiederherstellung",
    es: "Copia de seguridad y restauración", it: "Backup e ripristino",
    pt: "Cópia de segurança e restauro", "pt-BR": "Backup e restauração",
    ru: "Резервное копирование и восстановление", ar: "النسخ الاحتياطي والاستعادة",
    hi: "बैकअप और पुनर्स्थापना", id: "Cadangkan & pulihkan",
  },
  "backup.desc": {
    ko: "모든 캐릭터·대화·페르소나·에셋을 단일 파일로 내보내고 복원합니다. (API 키는 보안상 포함되지 않습니다)",
    "en-US": "Export a local backup file or import backup data into this browser. Import merges rows and overwrites matching items; it does not delete local items absent from the file. API keys are excluded for security.",
    ja: "すべてのキャラクター・チャット・ペルソナ・アセットを 1 つのファイルでエクスポート・復元します。（API キーはセキュリティ上含まれません）",
    zh: "将所有角色、对话、人格与素材导出和恢复为单个文件。（出于安全考虑不包含 API 密钥）",
    fr: "Exportez et restaurez tous les personnages, discussions, personas et ressources dans un seul fichier. (Les clés API sont exclues pour des raisons de sécurité.)",
    de: "Exportiere und stelle alle Charaktere, Chats, Personas und Assets als einzelne Datei wieder her. (API-Schlüssel werden aus Sicherheitsgründen ausgeschlossen.)",
    es: "Exporta y restaura todos los personajes, chats, personas y recursos en un solo archivo. (Las claves API se excluyen por seguridad.)",
    it: "Esporta e ripristina tutti i personaggi, le chat, le persona e gli asset in un unico file. (Le chiavi API sono escluse per sicurezza.)",
    pt: "Exporte e restaure todos os personagens, conversas, personas e recursos num único ficheiro. (As chaves API são excluídas por segurança.)",
    "pt-BR": "Exporte e restaure todos os personagens, conversas, personas e recursos em um único arquivo. (As chaves de API são excluídas por segurança.)",
    ru: "Экспортируйте и восстанавливайте всех персонажей, чаты, персоны и ресурсы одним файлом. (API-ключи исключены из соображений безопасности.)",
    ar: "صدّر واستعد جميع الشخصيات والمحادثات والشخصيات الافتراضية والأصول في ملف واحد. (تُستثنى مفاتيح API لأسباب أمنية.)",
    hi: "सभी पात्र, चैट, पर्सोना और एसेट को एकल फ़ाइल में निर्यात और पुनर्स्थापित करें। (सुरक्षा के लिए API कुंजियाँ शामिल नहीं हैं।)",
    id: "Ekspor dan pulihkan semua karakter, obrolan, persona, dan aset sebagai satu file. (Kunci API dikecualikan demi keamanan.)",
  },
  "backup.exportDone": {
    ko: "백업 파일을 저장했습니다.", "en-US": "Backup file downloaded.",
    ja: "バックアップファイルをダウンロードしました。", zh: "已下载备份文件。",
    fr: "Fichier de sauvegarde téléchargé.", de: "Sicherungsdatei heruntergeladen.",
    es: "Archivo de copia de seguridad descargado.", it: "File di backup scaricato.",
    pt: "Ficheiro de cópia de segurança descarregado.", "pt-BR": "Arquivo de backup baixado.",
    ru: "Файл резервной копии загружен.", ar: "تم تنزيل ملف النسخة الاحتياطية.",
    hi: "बैकअप फ़ाइल डाउनलोड हो गई।", id: "File cadangan telah diunduh.",
  },
  "backup.exportFail": {
    ko: "내보내기 실패: {error}", "en-US": "Export failed: {error}",
    ja: "エクスポートに失敗しました: {error}", zh: "导出失败：{error}",
    fr: "Échec de l'exportation : {error}", de: "Export fehlgeschlagen: {error}",
    es: "Error al exportar: {error}", it: "Esportazione non riuscita: {error}",
    pt: "Falha na exportação: {error}", "pt-BR": "Falha na exportação: {error}",
    ru: "Не удалось экспортировать: {error}", ar: "فشل التصدير: {error}",
    hi: "निर्यात विफल: {error}", id: "Ekspor gagal: {error}",
  },
  "backup.restoreConfirm": {
    ko: "백업을 복원하면 기존 데이터가 덮어씌워집니다. 계속하시겠어요?",
    "en-US": "Import backup data? Matching items may be overwritten, but local items absent from the backup are not deleted.",
    ja: "バックアップを復元すると、一致する項目は上書きされます。続行しますか？",
    zh: "恢复备份会覆盖相同的项目。是否继续？",
    fr: "La restauration d'une sauvegarde écrase les éléments correspondants. Continuer ?",
    de: "Beim Wiederherstellen einer Sicherung werden übereinstimmende Elemente überschrieben. Fortfahren?",
    es: "Restaurar una copia de seguridad sobrescribe los elementos coincidentes. ¿Continuar?",
    it: "Il ripristino di un backup sovrascrive gli elementi corrispondenti. Continuare?",
    pt: "Restaurar uma cópia de segurança substitui os itens correspondentes. Continuar?",
    "pt-BR": "Restaurar um backup sobrescreve os itens correspondentes. Continuar?",
    ru: "При восстановлении из резервной копии совпадающие элементы будут перезаписаны. Продолжить?",
    ar: "تؤدي استعادة النسخة الاحتياطية إلى استبدال العناصر المطابقة. هل تريد المتابعة؟",
    hi: "बैकअप पुनर्स्थापित करने से मेल खाते आइटम अधिलेखित हो जाएँगे। जारी रखें?",
    id: "Memulihkan cadangan akan menimpa item yang sama. Lanjutkan?",
  },
  "backup.restoreDone": {
    ko: "복원이 완료되었습니다. 변경 사항을 보려면 새로고침하세요.",
    "en-US": "Backup data imported. Refresh to apply changes.",
    ja: "復元が完了しました。変更を反映するには再読み込みしてください。",
    zh: "恢复完成。请刷新以应用更改。",
    fr: "Restauration terminée. Actualisez pour appliquer les modifications.",
    de: "Wiederherstellung abgeschlossen. Zum Übernehmen neu laden.",
    es: "Restauración completa. Actualiza para aplicar los cambios.",
    it: "Ripristino completato. Aggiorna per applicare le modifiche.",
    pt: "Restauro concluído. Atualize para aplicar as alterações.",
    "pt-BR": "Restauração concluída. Atualize para aplicar as alterações.",
    ru: "Восстановление завершено. Обновите страницу, чтобы применить изменения.",
    ar: "اكتملت الاستعادة. حدّث الصفحة لتطبيق التغييرات.",
    hi: "पुनर्स्थापना पूर्ण। बदलाव लागू करने हेतु रिफ़्रेश करें।",
    id: "Pemulihan selesai. Segarkan untuk menerapkan perubahan.",
  },
  "backup.restoreFail": {
    ko: "복원 실패: {error}", "en-US": "Backup import failed: {error}",
    ja: "復元に失敗しました: {error}", zh: "恢复失败：{error}",
    fr: "Échec de la restauration : {error}", de: "Wiederherstellung fehlgeschlagen: {error}",
    es: "Error al restaurar: {error}", it: "Ripristino non riuscito: {error}",
    pt: "Falha no restauro: {error}", "pt-BR": "Falha na restauração: {error}",
    ru: "Не удалось восстановить: {error}", ar: "فشلت الاستعادة: {error}",
    hi: "पुनर्स्थापना विफल: {error}", id: "Pemulihan gagal: {error}",
  },
} satisfies Record<string, Row>;
